"use client";
import AttachmentIcon from "@/assets/icons/attachment.svg";
import CameraIcon from "@/assets/icons/camera.svg";
import ChatIcon from "@/assets/icons/chat.svg";
import { Button } from "@/app/(components)/Basic/Button";
import { FeedPostDto, FeedPostCreate, FeedPostImage } from "@/api/posts/types";
import * as Yup from "yup";
import { Form, Formik, FormikHelpers } from "formik";
import usePostCreate from "../hooks/usePostCreate";
import useMe from "@/shared/hooks/useMe";
import { twMerge } from "tailwind-merge";
import React, { useCallback, useMemo, useState } from "react";
import { MEDIA_TYPE, SUPPORTED_IMAGE_TYPES } from "@/shared/media";
import { uploadPostImage } from "@/api/upload";
import { FormikPostUpdate, UploadedImage } from "../types";
import nftsApi from "@/api/nfts/client";
import { FeedUpsertImages } from "../FeedUpsertImages";
import { UploadFiles } from "@/app/(components)/Basic/UploadFiles";
import { FeedAttachNft } from "../FeedAttachNft";
import Link from "next/link";
import { FeedPostContent } from "../FeedPostContent";
import { useQuery } from "@tanstack/react-query";
import { FormContentEditable } from "@/app/(components)/Basic/ContentEditable";
import useAttachedNftRouteQuery from "../hooks/useAttachedNftRouteQuery";
import FeedOgData from "../FeedOgData/FeedOgData";
import { OgResult } from "@/shared/types";
import useTextRouteQuery from "../hooks/useTextRouteQuery";

export interface Props {
  id: string;
  feedPost?: FeedPostDto;
  repost?: FeedPostDto;
  onSubmit?: () => void;
  onSuccess?: () => void;
  onError?: () => void;
  hideAttachActions?: boolean;
  forceLightTheme?: boolean;
}

const MAX_GALLERY_SIZE = 10;

export default function FeedUpsert({
  id,
  feedPost,
  repost,
  onSubmit,
  onSuccess,
  onError,
  hideAttachActions,
  forceLightTheme,
}: Props) {
  const { createPost, isLoading } = usePostCreate({ onSuccess, onError });
  const [isAttachArtworkSectionVisible, setIsAttachArtworkSectionVisible] =
    useState<boolean>(false);
  const { me } = useMe();

  const [ogData, setOgData] = useState<OgResult[]>([]);
  const [ogIgnoredLinks, setOgIgnoredLinks] = useState<string[]>([]);

  const { attachedNftId, setAttachedNftId } = useAttachedNftRouteQuery();
  const { text, setText } = useTextRouteQuery();

  const { data: createdNftsByUser } = useQuery({
    queryKey: ["portfolio", me?.address],
    queryFn: async () => {
      if (!me?.address) return [];

      return await nftsApi.getNftsByArtist({
        artistId: me?.address,
        skip: 0,
        take: 50,
      });
    },
    enabled: !!me?.address,
  });

  const { data: attachedNft } = useQuery({
    queryKey: ["nft", attachedNftId],
    queryFn: () => nftsApi.getSingleNft(attachedNftId!),
    enabled: !!attachedNftId,
  });

  const { data: ownedNftsByUser } = useQuery({
    queryKey: ["owned", me?.address],
    queryFn: async () => {
      if (!me?.address) return [];
      return await nftsApi.getNftsByOwner(me?.address, 0, 50);
    },
    enabled: !!me?.address,
  });

  const validationSchema = () =>
    Yup.object().shape({
      text: Yup.string()
        .trim()
        .max(1000, "Content may not be longer than 1000 characters")
        .when(
          ["gallery", "attachedNftId", "repostId"],
          function (vals, schema) {
            const [gallery, attachedNftId, repostId] = vals;

            if (!gallery?.length && !attachedNftId && !repostId) {
              return schema.required("Add text or images");
            }
            return schema;
          },
        ),
      gallery: Yup.array().max(MAX_GALLERY_SIZE),
      attachedNftId: Yup.string().nullable(),
      repostId: Yup.string().nullable(),
    });

  const uploadImages = async (images: UploadedImage[]) => {
    const promises: Promise<FeedPostImage>[] = [];

    images.map((image) => {
      if (image && image.file instanceof File) {
        promises.push(uploadPostImage(image.file));
      } else {
        promises.push(
          Promise.resolve({
            id: image.id,
            imageUrl: image.imageUrl!,
            imagePlaceholder: image.imagePlaceholder,
          }),
        );
      }
    });

    const uploadedImages = await Promise.all(promises);
    return uploadedImages;
  };

  const onFormSubmit = useCallback(
    async (
      values: FormikPostUpdate,
      helpers: FormikHelpers<FormikPostUpdate>,
    ) => {
      onSubmit?.();
      const uploadedImages =
        values.gallery && (await uploadImages(values.gallery));

      await createPost({
        ...values,
        repostId: values.repostId,
        attachedNftId: values.attachedNftId || null,
        text: values.text?.trim(),
        gallery: uploadedImages,
        ignoredLinks: ogIgnoredLinks,
      } as FeedPostCreate);

      helpers.resetForm();
      setAttachedNftId("");
      setText("");
    },
    [createPost, onSubmit, ogIgnoredLinks],
  );

  const allUserNfts = useMemo(
    () =>
      [
        ...Array.from(createdNftsByUser || []),
        ...Array.from(ownedNftsByUser || []),
        attachedNft,
      ].filter(Boolean),
    [attachedNft, createdNftsByUser, ownedNftsByUser],
  );

  const onRemoveLink = useCallback((link: string) => {
    setOgIgnoredLinks((links) => [...links, link]);
  }, []);

  const onLinkDetect = useCallback(
    async (links: string[]) => {
      if (links.length === 0) {
        setOgData([]);
        return;
      }

      const filtered = links.filter((l) => !ogIgnoredLinks.includes(l));

      const promises = filtered.map(async () => {
        const result = await fetch(`/api/ogs?url=${filtered[0]}`);
        const data = await result.json();

        return {
          ...data,
          link: filtered[0],
        };
      });

      const data = await Promise.all(promises);
      setOgData(data);
    },
    [ogIgnoredLinks],
  );

  if (!me) {
    return null;
  }

  if (me && !me.displayName && !me.fullName) {
    return (
      <Link
        href="/settings"
        className="block text-body-2 text-green endemic:text-primary underline w-full text-center"
      >
        You need to set your full name or username to be able to post feed.
      </Link>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={
          {
            id: feedPost?.id || "",
            text: text || feedPost?.text || "",
            gallery: feedPost?.gallery || [],
            attachedNftId: attachedNftId || feedPost?.attachedNftId || null,
            repostId: feedPost?.repostId || repost?.id,
          } as FormikPostUpdate
        }
        validationSchema={validationSchema}
        onSubmit={onFormSubmit}
      >
        {({ values, isValid, setValues, errors, isSubmitting }) => {
          const attachedNft = allUserNfts?.find(
            (nft) => nft?.id == values.attachedNftId,
          );

          const isPostDisabled =
            isLoading || !me || (!me?.displayName && !me.fullName) || !isValid;

          let attachedData = null;
          if (attachedNft) {
            attachedData = (
              <FeedUpsertImages
                id={id}
                images={[
                  {
                    id: "0",
                    imageUrl: attachedNft.image,
                    imagePlaceholder: attachedNft.placeholder,
                    mediaType: attachedNft.mediaType!,
                  },
                ]}
                onImageRemove={() =>
                  setValues({ ...values, attachedNftId: undefined })
                }
              />
            );
          } else if (values.gallery?.length) {
            attachedData = (
              <FeedUpsertImages
                id={id}
                images={values.gallery?.map((img) => ({
                  ...img,
                  mediaType: MEDIA_TYPE.IMAGE,
                }))}
                onImageRemove={(id) => {
                  setValues({
                    ...values,
                    gallery: values.gallery?.filter((i) => i.id !== id),
                  });
                }}
                isErrorVisible={
                  values?.gallery && values.gallery.length > MAX_GALLERY_SIZE
                }
                maxGallerySize={MAX_GALLERY_SIZE}
                displayAddButton
              />
            );
          } else if (!!ogData) {
            const firstOg = ogData.find(
              (d) => !ogIgnoredLinks.includes(d.link),
            );
            if (firstOg) {
              attachedData = (
                <FeedOgData {...firstOg} onRemove={onRemoveLink} />
              );
            }
          }

          return (
            <>
              <Form id={id}>
                <div>
                  <FormContentEditable
                    id={`${id}-text`}
                    name="text"
                    placeholder="Type your message here..."
                    disabled={!me}
                    errorMsgClassName="py-4"
                    onLinkDetect={onLinkDetect}
                    maxLength={1000}
                    forceLightTheme={forceLightTheme}
                  />

                  <div className="flex flex-row flex-wrap justify-between mt-0.5">
                    {attachedData}
                  </div>

                  {!!repost && (
                    <FeedPostContent
                      feedPost={repost}
                      hideActions={true}
                      className="mb-4 border p-4"
                      forceLightTheme={forceLightTheme}
                    />
                  )}
                  <div
                    className={twMerge(
                      "flex flex-row justify-between items-center",
                      ((values?.gallery && values?.gallery?.length > 0) ||
                        attachedNft) &&
                        "border-t mt-5 pt-4",
                    )}
                  >
                    {!hideAttachActions && !feedPost?.repostId && (
                      <div className="flex flex-row gap-x-3 md:gap-x-4 text-body-4 text-endemicGray-700">
                        {allUserNfts && allUserNfts.length > 0 && (
                          <Button
                            native
                            type="button"
                            startIcon={
                              <AttachmentIcon
                                className={twMerge(
                                  "w-5 mr-1.5  fill-endemicGray-700",
                                  !me && "opacity-60",
                                  !forceLightTheme && "endemic:fill-primary",
                                )}
                              />
                            }
                            className="flex items-center text-body-4 pointer"
                            disabled={!me}
                            onClick={() =>
                              setIsAttachArtworkSectionVisible(
                                !isAttachArtworkSectionVisible,
                              )
                            }
                          >
                            <label
                              className={twMerge(
                                "hidden lg:block text-body-4 cursor-pointer",
                                !me && "opacity-60 cursor-default",
                                !forceLightTheme && "endemic:text-primary",
                              )}
                            >
                              Attach artwork
                            </label>
                          </Button>
                        )}

                        <UploadFiles
                          id={`${id}-uploadGallery`}
                          fieldName="gallery"
                          title="Attach image"
                          startIcon={
                            <CameraIcon
                              className={twMerge(
                                "w-5 h-4 fill-endemicGray-700",
                                !forceLightTheme && "endemic:fill-primary",
                              )}
                            />
                          }
                          className={twMerge(
                            "flex items-center text-endemicGray-700",
                            !me && "opacity-60",
                          )}
                          titleClassName={twMerge(
                            "text-endemicGray-700 text-body-4",
                            !forceLightTheme && "endemic:text-primary",
                          )}
                          disabled={!me}
                          maxFileSize={5}
                          accept={SUPPORTED_IMAGE_TYPES}
                          multiple
                        />
                      </div>
                    )}
                    {errors.gallery || errors.attachedNftId ? (
                      <div className="mx-auto text-red text-xs">
                        {errors.gallery || errors.attachedNftId}
                      </div>
                    ) : null}
                    <Button
                      disabled={isSubmitting || isPostDisabled}
                      native
                      type="submit"
                      startIcon={
                        <ChatIcon
                          className={twMerge(
                            "w-6 h-6 fill-red disabled",
                            isPostDisabled &&
                              "fill-endemicGray-700 cursor-default opacity-60",
                            !forceLightTheme && "endemic:fill-primary",
                          )}
                        />
                      }
                      className="flex ml-auto gap-2 items-center text-body-4 cursor-pointer text-red disabled:text-endemicGray-700 disabled:opacity-60"
                    >
                      <span
                        className={twMerge(
                          "text-body-4",
                          !forceLightTheme && "endemic:text-primary",
                        )}
                      >
                        Post
                      </span>
                    </Button>
                  </div>
                </div>
                <FeedAttachNft
                  isOpen={isAttachArtworkSectionVisible}
                  onClose={() => setIsAttachArtworkSectionVisible(false)}
                  ownedNfts={ownedNftsByUser || []}
                  createdNfts={createdNftsByUser || []}
                  initialAttachedNftId={values.attachedNftId}
                  onChange={(attachedNftId) => {
                    setValues({ ...values, attachedNftId, gallery: [] });
                  }}
                />
              </Form>
            </>
          );
        }}
      </Formik>
    </>
  );
}
